import {ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges, ViewChild} from '@angular/core';
import {DatePipe} from '@angular/common';
import {NgbModal, NgbPaginationModule} from '@ng-bootstrap/ng-bootstrap';
import {ToastrService} from 'ngx-toastr';
import {
    isOrganisationActive,
    isOrganisationDeactivated,
    isOrganisationDeleted,
    OrganisationDetail,
    OrganisationLinkedService,
    OrganisationLogEntry
} from '@shared/api/models/query/organisation.model';
import {OrganisationsService} from '@shared/api/services/v2/organisations.service';
import {isAlreadyNotified} from '@shared/api/services/v2/api-request';
import {Pagination} from '@models/pagination.model';
import {Permissions} from '@models/permissions.enum';
import {PermissionRolesMap} from '@models/permissions.db';
import {AuthService} from '@shared/services/security/auth.service';
import {ConfirmDialogDirective} from '@shared/components/confirm-dialog/confirm-dialog.directive';
import {SpinnerComponent} from '@shared/components/spinner/spinner.component';
import {DeactivateOrganisationModal} from '../modals/deactivate-organisation.modal';

/**
 * UNIVAPLAT-3699: History tab of the organisation detail page. The detail page only renders it for
 * users holding VIEW_ORGANISATION_HISTORY (general admins).
 *
 * - Server-side paginated list of log entries, newest first (reasons are shown as plain text).
 * - Deactivate (optional reason) and Reactivate (confirm) are offered only to CHANGE_ORGANISATION_STATE
 *   holders, and only while the state allows it (never for deleted organisations).
 * - Deactivate is disabled while the page reports linked services (the API answers 422 anyway).
 * - After a successful change the history reloads and `changed` tells the detail page to reload the
 *   organisation (badge, read-only form, tabs, linked lists).
 * - The backend owns the error text, shown verbatim. Reasons are personal data: never logged and
 *   dropped on destroy.
 */
@Component({
    selector: 'cdss-organisation-history-tab',
    standalone: true,
    imports: [DatePipe, NgbPaginationModule, ConfirmDialogDirective, SpinnerComponent],
    templateUrl: './organisation-history-tab.component.html',
    styleUrl: './organisation-history-tab.component.scss'
})
export class OrganisationHistoryTabComponent implements OnChanges, OnDestroy {
    @Input() organisation!: OrganisationDetail;
    /** Linked services of the organisation, from the detail page. */
    @Input() linkedServices: OrganisationLinkedService[] = [];
    /** Emits after a deactivate / reactivate landed (or the API says the state had already changed). */
    @Output() changed = new EventEmitter<void>();

    static readonly PAGE_SIZE = 10;

    entries: OrganisationLogEntry[] = [];
    loading = false;
    loadError: string | null = null;
    /** 1-based page for ngb-pagination. */
    page = 1;
    total = 0;
    readonly pageSize = OrganisationHistoryTabComponent.PAGE_SIZE;
    /** Double-submit guard for the reactivate request. */
    reactivating = false;

    @ViewChild('stateButton') private stateButton?: ElementRef<HTMLButtonElement>;

    private loadSeq = 0;

    constructor(
        private organisationsService: OrganisationsService,
        private authService: AuthService,
        private modalService: NgbModal,
        private toastr: ToastrService,
        private cdr: ChangeDetectorRef,
    ) {
    }

    get canChangeState(): boolean {
        const userRoles = this.authService.roles ?? [];
        return (PermissionRolesMap[Permissions.CHANGE_ORGANISATION_STATE] ?? []).some(role => userRoles.includes(role));
    }

    get canDeactivate(): boolean {
        return !!this.organisation && isOrganisationActive(this.organisation);
    }

    get canReactivate(): boolean {
        return !!this.organisation && !isOrganisationDeleted(this.organisation) && isOrganisationDeactivated(this.organisation);
    }

    get blockedByServices(): boolean {
        return this.linkedServices.length > 0;
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!changes['organisation']) {
            return;
        }
        const previous = changes['organisation'].previousValue as OrganisationDetail | undefined;
        const current = this.organisation;
        if (!previous || previous.id !== current?.id) {
            this.page = 1;
            void this.load();
        } else if (!!previous.disabledAt !== !!current.disabledAt || !!previous.deletedAt !== !!current.deletedAt) {
            // State flipped elsewhere (e.g. a refused save): show the new entry.
            this.page = 1;
            void this.load();
        }
    }

    ngOnDestroy(): void {
        this.loadSeq++;
        this.entries = [];
    }

    async load(): Promise<void> {
        const seq = ++this.loadSeq;
        if (!this.organisation) {
            return;
        }
        this.loading = true;
        this.loadError = null;
        try {
            const result = await this.organisationsService.listLogs(
                this.organisation.id,
                new Pagination(this.pageSize, this.page - 1));
            if (seq === this.loadSeq) {
                this.entries = result.data;
                this.total = result.pagination?.totalCount ?? result.data.length;
            }
        } catch (error: any) {
            if (seq === this.loadSeq) {
                this.entries = [];
                this.total = 0;
                this.loadError = error?.error?.error || 'The organisation history could not be loaded.';
            }
        } finally {
            if (seq === this.loadSeq) {
                this.loading = false;
            }
        }
    }

    onPageChange(page: number): void {
        this.page = page;
        void this.load();
    }

    /** Unknown actions fall back to the raw value so a new backend action never breaks the table. */
    actionLabel(entry: OrganisationLogEntry): string {
        const title = entry.careProgram?.title;
        switch (entry.action) {
            case 'deactivate':
                return 'Deactivated';
            case 'reactivate':
                return 'Reactivated';
            case 'delete':
                return 'Deleted';
            case 'link_care_program':
                return title ? `Care programme linked: ${title}` : 'Care programme linked';
            case 'unlink_care_program':
                return title ? `Care programme unlinked: ${title}` : 'Care programme unlinked';
            default:
                return String(entry.action);
        }
    }

    async openDeactivateModal(): Promise<void> {
        if (!this.canChangeState || !this.canDeactivate || this.blockedByServices) {
            return;
        }
        const ref = this.modalService.open(DeactivateOrganisationModal, {
            centered: true,
            ariaLabelledBy: 'deactivate-organisation-title',
            ariaDescribedBy: 'deactivate-organisation-description'
        });
        ref.componentInstance.organisationId = this.organisation.id;
        const done = await ref.result.catch(() => false);
        if (done) {
            await this.afterChange();
        } else {
            this.focusStateButton();
        }
    }

    async reactivate(): Promise<void> {
        if (this.reactivating || !this.canChangeState || !this.canReactivate) {
            return;
        }
        this.reactivating = true;
        try {
            await this.organisationsService.reactivate(this.organisation.id);
            this.toastr.success('Organisation reactivated', 'Success');
            await this.afterChange();
        } catch (error: any) {
            if (!isAlreadyNotified(error)) {
                this.toastr.error(error?.error?.error || error?.error?.message || 'Something went wrong. Please try again.');
            }
            if (error?.status === 409) {
                // Already reactivated meanwhile: refresh the state.
                await this.afterChange();
            }
        } finally {
            this.reactivating = false;
        }
    }

    private async afterChange(): Promise<void> {
        this.page = 1;
        await this.load();
        this.changed.emit();
        this.focusStateButton();
    }

    /** The state button is replaced by the opposite action after a change; return focus to whichever is shown. */
    private focusStateButton(): void {
        this.cdr.detectChanges();
        this.stateButton?.nativeElement.focus();
    }
}
